import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import Loader from "../Loader";
import KeycloakContext from "../../context";
import { initKeycloak } from "../../keycloakAxios";
import { useUserActivity } from "../../hooks";
import { isServer } from "../../utils/other";

const useInitKeycloak = ({ configUrl, options }) => {
  const [keycloak, setKeycloak] = useState();
  const [initError, setInitError] = useState(false);

  useEffect(() => {
    let isSubscribed = true;
    (async function init() {
      // eslint-disable-next-line no-underscore-dangle
      let _keycloak;
      try {
        if (!isServer()) {
          _keycloak = await initKeycloak(configUrl, options);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[Keycloak]: Failed to initialize keycloak", err);
        if (isSubscribed) {
          setInitError(true);
        }
      }
      if (isSubscribed) {
        setKeycloak(_keycloak);
      }
    })();

    return () => {
      isSubscribed = false;
    };
    // Inititialization should happen only on mount component
    // eslint-disable-next-line
  }, []);

  return { keycloak, initError };
};

const KeycloakProvider = ({
  children,
  loader,
  configUrl,
  options,
  onLogout,
  trackUserActivity,
  sessionIdleMilliseconds,
}) => {
  const [logoutTimer, setLogoutTimer] = useState();
  const { keycloak, initError } = useInitKeycloak({ configUrl, options });

  useUserActivity(() => {
    if (!keycloak) {
      return;
    }
    if (trackUserActivity) {
      keycloak.validateToken();
    }

    if (logoutTimer) {
      clearTimeout(logoutTimer);
    }

    if (sessionIdleMilliseconds) {
      setLogoutTimer(
        setTimeout(() => keycloak.validateToken(), sessionIdleMilliseconds)
      );
    }
  }, 10000);

  useEffect(() => {
    if (!keycloak) {
      return;
    }

    keycloak.onAuthLogout = () => {
      console.log("[Keycloak]: Session expired or logout triggered");

      // Clear any pending logout timers
      if (logoutTimer) {
        clearTimeout(logoutTimer);
        setLogoutTimer(undefined);
      }

      if (onLogout) {
        // for what case need skip native keycloak logout
        onLogout();
      } else {
        keycloak.logout();
      }
    };

    // Handle auth refresh errors (token expired and couldn't refresh)
    const originalOnAuthRefreshError = keycloak.kc.onAuthRefreshError;
    keycloak.kc.onAuthRefreshError = () => {
      console.error("[Keycloak]: Token refresh failed, session expired");
      if (originalOnAuthRefreshError) {
        originalOnAuthRefreshError();
      }
      // Trigger logout
      if (keycloak.onAuthLogout) {
        setTimeout(keycloak.onAuthLogout, 0);
      }
    };
  }, [keycloak, onLogout, logoutTimer]);

  const context = keycloak
    ? {
        getUserData: keycloak.getUserData,
        keycloak: keycloak.kc,
        // Remove isAuthenticated next release
        isAuthenticated: keycloak.getDeprecatedAuthenticated(),
        login: keycloak.login,
        logout: keycloak.logout,
      }
    : null;

  // If there was an error initializing Keycloak, show error message
  if (initError) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          padding: "20px",
          textAlign: "center",
        }}
      >
        <h2>Authentication Error</h2>
        <p>
          Failed to initialize authentication system. Please refresh the page or
          contact support.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: "20px",
            padding: "10px 20px",
            cursor: "pointer",
          }}
        >
          Refresh Page
        </button>
      </div>
    );
  }

  return (
    <KeycloakContext.Provider value={context}>
      {typeof children === "function"
        ? children(context)
        : (keycloak && children) || loader}
    </KeycloakContext.Provider>
  );
};

KeycloakProvider.displayName = "KeycloakProvider";

KeycloakProvider.defaultProps = {
  loader: <Loader />,
  onLogout: undefined,
  trackUserActivity: false,
  options: {
    onLoad: "check-sso",
  },
  sessionIdleMilliseconds: undefined,
};

KeycloakProvider.propTypes = {
  /** Anything that can be rendered  */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  /** React element that can be rendered when user in authentication process */
  loader: PropTypes.element,
  /** Url of the keycloak adapter configurations json file */
  configUrl: PropTypes.string.isRequired,
  /** Keycloak js provider configurations  */
  options: PropTypes.object,
  /** If onLogout is passed, in case of token expired onLogout will be called, otherwise redirected to login page. */
  onLogout: PropTypes.func,
  /** Track "mousedown", "mousemove", "keypress", "scroll", "touchmove" events and refresh auth token */
  trackUserActivity: PropTypes.bool,
  /** SSO Session Idle logout after this time when the user is not active (Need to set the same time, which is specified in the administrative module Keycloak and need to specify "trackUserActivity") */
  sessionIdleMilliseconds: PropTypes.number,
};

export default KeycloakProvider;
